import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import { ScoreBookingLink } from "@/components/ScoreBookingLink";
import SEOHead from "@/components/SEOHead";
import { getServiceSchemaSupportingEntities } from "@/lib/siteEntityGraph";

import SchemaScript from "@/components/SchemaScript";
const CANONICAL = "https://integrated.social/services/ai-citation-programme/";
const INTRO_DESCRIPTION =
  "Includes an AI Visibility Audit at the start, at no extra charge (bought on its own, the audit is from £750 + VAT, one-off). AI Citation Programme (our Foundation programme). Introductory price: £2,200 per month + VAT for your first 3 months, for programmes started by 28 December 2026. Standard price: £3,000 per month + VAT.";

const faqs = [
  {
    question: "What is the AI Citation Programme?",
    answer:
      "The AI Citation Programme is Integrated.Social's Foundation programme under a clearer name. Each month we work on a priority set of your pages so ChatGPT, Gemini and other AI assistants can find, understand and trust what you say, backed by credible evidence. A named senior person reviews the work. No one controls which sources AI assistants cite.",
  },
  {
    question: "What does the introductory price include, and when does it end?",
    answer:
      "It is the monthly fee for the same Foundation programme. Includes an AI Visibility Audit at the start, at no extra charge (bought on its own, the audit is from £750 + VAT, one-off). AI Citation Programme (our Foundation programme). Introductory price: £2,200 per month + VAT for your first 3 months, for programmes started by 28 December 2026. Standard price: £3,000 per month + VAT. The offer ends on 28 December 2026 and is not extended. The minimum term is 3 months, then 30 days' notice.",
  },
  {
    question: "Does the programme guarantee that AI assistants will cite us?",
    answer:
      "No. ChatGPT, Gemini and Google decide which sources they show, and answers change between users and runs. We make your pages, evidence and entity details clearer and easier to check, then track mentions on fixed buyer questions over time. Treat any promised citation, ranking or date from any agency as a warning sign.",
  },
  {
    question:
      "How is the AI Citation Programme different from the AI Visibility Audit?",
    answer:
      "The AI Citation Programme includes an AI Visibility Audit at the start, at no extra charge. Bought on its own, the AI Visibility Audit, from £750 + VAT (one-off), is a senior-led diagnostic that reviews your priority pages, technical set-up and structured data, with remediation direction. The AI Citation Programme is the ongoing monthly work that acts on findings like these, across AI visibility, technical foundations, buyer-question content and credible evidence.",
  },
  {
    question: "What happens after the first 3 months?",
    answer:
      "For programmes started by 28 December 2026, the introductory price covers your first 3 months. After that the standard price applies: £3,000 per month + VAT, for the same Foundation scope. The minimum term is 3 months, then 30 days' notice, and we confirm both in writing before you start.",
  },
  {
    question:
      "How is the Free AI Visibility Score different from the programme?",
    answer:
      "The Free AI Visibility Score is a self-serve 0–100 baseline of how clearly your site is set up for AI answers, with no credit card needed. It is a starting point, not a plan. If it shows gaps worth fixing, talk it through with Modi and we will confirm whether the programme fits.",
  },
  {
    question: "Who reviews the work?",
    answer:
      "Modi Elnadi, founder of Integrated.Social, leads the programme. Agents handle research, first drafts and monitoring, and a named senior person reviews every page change, claim and piece of evidence before it goes live. Modi brings a design background, an MBA and 20+ years in digital marketing.",
  },
] as const;

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    ...getServiceSchemaSupportingEntities(),
    {
      "@type": "WebPage",
      "@id": CANONICAL,
      url: CANONICAL,
      name: "Get Cited by AI? Improve your chances in ChatGPT and Gemini answers",
      headline:
        "Get Cited by AI? Improve your chances in ChatGPT and Gemini answers",
      description:
        "The AI Citation Programme is our Foundation plan: senior-led AEO so ChatGPT, Gemini and AI Overviews can find, trust and cite your site. No guarantees.",
      abstract: faqs[0].answer,
      inLanguage: "en-US",
      author: { "@id": "https://integrated.social/modi-elnadi/#person" },
      isPartOf: { "@id": "https://integrated.social/#website" },
      breadcrumb: { "@id": `${CANONICAL}#breadcrumb` },
      mainEntity: { "@id": `${CANONICAL}#service` },
      dateModified: "2026-10-26",
    },
    {
      "@type": "Service",
      "@id": `${CANONICAL}#service`,
      name: "AI Citation Programme (Foundation)",
      alternateName: "Foundation programme",
      serviceType: "Answer engine optimization programme",
      url: CANONICAL,
      description:
        "Integrated.Social's Foundation programme, launched as the AI Citation Programme: monthly, senior-reviewed work on priority pages, entity details, structured data and evidence, toward being easier for AI assistants to cite. Citations are not guaranteed.",
      provider: { "@id": "https://integrated.social/#organization" },
      areaServed: "GB",
      offers: [
        { "@id": `${CANONICAL}#offer-intro` },
        { "@id": `${CANONICAL}#offer-standard` },
      ],
    },
    {
      "@type": "Offer",
      "@id": `${CANONICAL}#offer-intro`,
      name: "AI Citation Programme: introductory price",
      url: CANONICAL,
      price: 2200,
      priceCurrency: "GBP",
      priceValidUntil: "2026-12-28",
      availability: "https://schema.org/InStock",
      description: INTRO_DESCRIPTION,
      priceSpecification: [
        {
          "@type": "UnitPriceSpecification",
          price: 2200,
          priceCurrency: "GBP",
          unitCode: "MON",
          unitText: "MONTH",
          referenceQuantity: {
            "@type": "QuantitativeValue",
            value: 1,
            unitCode: "MON",
          },
          billingDuration: "P3M",
          valueAddedTaxIncluded: false,
          priceType: "https://schema.org/SalePrice",
          validThrough: "2026-12-28",
        },
        {
          "@type": "UnitPriceSpecification",
          price: 3000,
          priceCurrency: "GBP",
          unitCode: "MON",
          unitText: "MONTH",
          referenceQuantity: {
            "@type": "QuantitativeValue",
            value: 1,
            unitCode: "MON",
          },
          billingStart: 3,
          valueAddedTaxIncluded: false,
          priceType: "https://schema.org/ListPrice",
        },
      ],
      itemOffered: { "@id": `${CANONICAL}#service` },
      offeredBy: { "@id": "https://integrated.social/#organization" },
    },
    {
      "@type": "Offer",
      "@id": `${CANONICAL}#offer-standard`,
      name: "AI Citation Programme (Foundation): standard price",
      url: CANONICAL,
      price: 3000,
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      description: "Standard price: £3,000 per month + VAT.",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: 3000,
        priceCurrency: "GBP",
        unitCode: "MON",
        unitText: "MONTH",
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: 1,
          unitCode: "MON",
        },
        valueAddedTaxIncluded: false,
        priceType: "https://schema.org/ListPrice",
      },
      itemOffered: { "@id": `${CANONICAL}#service` },
      offeredBy: { "@id": "https://integrated.social/#organization" },
    },
    {
      "@type": "FAQPage",
      "@id": `${CANONICAL}#faq`,
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://integrated.social/services/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "AI Citation Programme",
          item: CANONICAL,
        },
      ],
    },
  ],
};

function focusScoreField() {
  document
    .getElementById("score-url-citation_hero")
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
  window.setTimeout(
    () => document.getElementById("score-url-citation_hero")?.focus(),
    350
  );
}


export default function ServiceAICitationProgramme() {
  const [isReportEmailReturn, setIsReportEmailReturn] = useState(false);
  useEffect(() => {
    setIsReportEmailReturn(new URLSearchParams(window.location.search).get("src") === "report_email");
  }, []);
  const bookingSource = isReportEmailReturn ? "report_email" as const : "hero_link" as const;
  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <SEOHead
        title="AI Citation Programme: Our Foundation AEO Plan, UK"
        description="The AI Citation Programme is our Foundation plan: senior-led AEO so ChatGPT, Gemini and AI Overviews can find, trust and cite your site. No guarantees."
        canonical={CANONICAL}
        locale="en-US"
        alternates={[{ hrefLang: "en-US", href: CANONICAL }, { hrefLang: "x-default", href: CANONICAL }]}
      />
      <SchemaScript schema={JSON.stringify(schema)} />
      <Header />
      <main>
        <div className="container px-4 pt-24">
          <Breadcrumbs
            includeSchema={false}
            items={[
              { label: "Services", href: "/services/" },
              { label: "AI Citation Programme" },
            ]}
          />
        </div>
        <header className="container grid gap-12 px-4 pb-16 pt-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-start lg:pt-20">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF8C00]">
              AI Citation Programme (our Foundation programme)
            </p>
            <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
              Get Cited by AI? Improve your chances in ChatGPT and Gemini answers
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/75 md:text-xl">
              We optimize your site and content so AI assistants can find, trust and cite it. No one can guarantee citations, and we don't.
            </p>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/70">
              {faqs[0].answer}
            </p>
            <div id="citation-score" className="mt-7 max-w-3xl">
              <ScoreInlineForm placement="citation_hero" />
            </div>
            <ScoreBookingLink id="book" context={{ placement: "citation_hero", nextIntent: "citation", pageType: "service", niche: "ai_citation_programme", bookingSource }} className="mt-4 inline-flex text-sm font-semibold text-[#FFB84D] underline underline-offset-4 hover:text-white">
              After your score, talk it through with Modi <ArrowRight className="ml-2 h-4 w-4" />
            </ScoreBookingLink>
            <p className="mt-8 text-sm text-white/60">
              20+ years in digital marketing · £20m+ in paid media managed · Design background and an MBA
            </p>
          </div>
          <aside id="intro-offer" className="rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/[0.06] p-6 md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#00D4AA]">
              Foundation programme
            </p>
            <p className="mt-5 text-sm leading-relaxed text-white/80">
              Includes an AI Visibility Audit at the start, at no extra charge (bought on its own, the audit is from £750 + VAT, one-off).
            </p>
            <p className="mt-4 text-lg font-semibold leading-relaxed text-white">
              AI Citation Programme (our Foundation programme). Introductory price: £2,200 per month + VAT for your first 3 months, for programmes started by 28 December 2026. Standard price: £3,000 per month + VAT.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-white/75">
              Save £800 a month (£2,200 vs £3,000 per month + VAT) for your first 3 months, on programmes started by 28 December 2026.
            </p>
            <p className="mt-4 text-sm text-white/75">
              Minimum term: 3 months, then 30 days&apos; notice.
            </p>
            <p className="mt-5 text-sm">
              <Link href="/free-ai-growth-audit/" className="text-[#FFB84D] underline underline-offset-4 hover:text-white">
                Free AI Visibility Score
              </Link>{" "}
              ·{" "}
              <Link href="/pricing/" className="text-[#FFB84D] underline underline-offset-4 hover:text-white">
                Foundation pricing
              </Link>
            </p>
          </aside>
        </header>

        <section className="border-y border-white/10 bg-white/[0.03] py-16">
          <div className="container max-w-5xl px-4">
            <h2 className="font-serif text-3xl">Who is the AI Citation Programme for, and who is it not for?</h2>
            <p className="mt-5 leading-relaxed text-white/70"><strong>For:</strong> B2B teams with a clear service and real evidence who want their priority pages easier for AI assistants to read, check and trust.</p>
            <p className="mt-3 leading-relaxed text-white/70"><strong>Not for:</strong> anyone looking for a promised citation, a quick trick, or content volume without review.</p>
            <ScoreLinks onFocus={focusScoreField} bookingSource={bookingSource} />
          </div>
        </section>

        <section className="container max-w-5xl px-4 py-16">
          <h2 className="font-serif text-3xl">What does the Foundation programme include each month?</h2>
          <p className="mt-5 max-w-4xl leading-relaxed text-white/70">Foundation is a focused AEO programme. It covers a priority set of pages, the questions your buyers ask and the visibility opportunities around them: AI visibility, technical foundations, buyer-question content and credible evidence. We confirm the exact scope after a short call.</p>
          <ScoreLinks onFocus={focusScoreField} bookingSource={bookingSource} />

          <h2 className="mt-16 font-serif text-3xl">How does the programme work?</h2>
          <ol className="mt-5 max-w-4xl list-decimal space-y-3 pl-5 leading-relaxed text-white/70">
            <li><strong>Score:</strong> your Free AI Visibility Score gives a 0–100 baseline.</li>
            <li><strong>Scope:</strong> after your score, a short call with Modi confirms which pages and buyer questions come first.</li>
            <li><strong>Monthly work:</strong> agents research and draft; we fix technical foundations, write answers to the questions buyers ask, and line up credible evidence on each priority page.</li>
            <li><strong>Review:</strong> a named senior person reviews every change before it goes live. We track mentions on a fixed set of buyer questions, run in fresh sessions and log them by date.</li>
          </ol>
          <p className="mt-5 text-white/70">We describe progress as readiness and dated samples, never as a citation count.</p>
          <ScoreLinks onFocus={focusScoreField} bookingSource={bookingSource} />

          <h2 className="mt-16 font-serif text-3xl">Can any agency guarantee AI citations?</h2>
          <p className="mt-5 max-w-4xl leading-relaxed text-white/70">No. ChatGPT, Gemini and Google decide which sources they show, and answers change between users and runs. What we can do is make your pages, evidence and entity details clearer and easier to check, then measure what happens. We don't publish citation-rate or more-citations figures.</p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <button type="button" onClick={focusScoreField} className="inline-flex min-h-11 items-center rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#0F1419] hover:bg-[#FFB84D]">Check my AI visibility</button>
            <ScoreBookingLink context={{ placement: "citation_hero", nextIntent: "citation", pageType: "service", niche: "ai_citation_programme", bookingSource }} className="text-sm font-semibold text-[#FFB84D] underline underline-offset-4 hover:text-white">After your score, talk it through with Modi</ScoreBookingLink>
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#101820] py-16">
          <div className="container max-w-5xl px-4">
            <h2 className="font-serif text-3xl">How much does it cost, and when does the introductory price end?</h2>
            <p className="mt-5 max-w-4xl leading-relaxed text-white/70">{INTRO_DESCRIPTION}</p>
            <p className="mt-4 max-w-4xl leading-relaxed text-white/70">Save £800 a month (£2,200 vs £3,000 per month + VAT) for your first 3 months. Minimum term: 3 months, then 30 days&apos; notice. · <Link href="/pricing/" className="text-[#FFB84D] underline underline-offset-4 hover:text-white">View all plans</Link></p>
            <p className="mt-4 text-white/70">The offer ends on 28 December 2026. It isn&apos;t extended, rolled over or shown with a countdown.</p>

            <h2 className="mt-16 font-serif text-3xl">What evidence can we show?</h2>
            <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead className="bg-white/[0.05] text-white"><tr><th className="p-4">Label</th><th className="p-4">Claim</th><th className="p-4">Scope / source</th></tr></thead>
                <tbody className="divide-y divide-white/10 text-white/70">
                  <tr><td className="p-4 font-semibold text-white">Verified result</td><td className="p-4">Our published B2B SaaS account-based marketing case study reports +312% qualified pipeline in 90 days; £2.4M pipeline in 6 months.</td><td className="p-4"><strong>ABM only</strong>, not an AI-citation result, and it doesn&apos;t predict one. Source: <Link href="/case-studies/b2b-saas-abm/" className="text-[#FFB84D] underline underline-offset-4">B2B SaaS ABM case study</Link>.</td></tr>
                  <tr><td className="p-4 font-semibold text-white">Author fact</td><td className="p-4">£20m+ in paid media managed; 20+ years in digital marketing; a design background and an MBA.</td><td className="p-4"><Link href="/modi-elnadi/" className="text-[#FFB84D] underline underline-offset-4">Modi Elnadi</Link></td></tr>
                  <tr><td className="p-4 font-semibold text-white">Future objective</td><td className="p-4">Priority pages that AI assistants can read and check more easily, with a dated baseline of mentions on fixed buyer questions.</td><td className="p-4">Aim only; not promised</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-5 text-sm text-white/60">No other figures are used. No +350%, 4.2x, 4.8x or 62% CPL, and no citation-rate figures.</p>
          </div>
        </section>

        <section id="author" className="container max-w-5xl px-4 py-16">
          <h2 className="font-serif text-3xl">Who does the work, and who reviews it?</h2>
          <p className="mt-5 max-w-4xl leading-relaxed text-white/70">Written and reviewed by <Link href="/modi-elnadi/" className="text-[#FFB84D] underline underline-offset-4 hover:text-white">Modi Elnadi</Link> · <a href="https://www.linkedin.com/in/modielnadi/" target="_blank" rel="noopener" className="text-[#FFB84D] underline underline-offset-4 hover:text-white">LinkedIn</a></p>
          <p className="mt-3 max-w-4xl leading-relaxed text-white/70">Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed. Agents draft; a named senior person reviews.</p>
          <p className="mt-6 text-sm text-white/70"><Link href="/services/preferred-sources-strategy/" className="text-[#00D4AA] underline underline-offset-4 hover:text-white">Preferred Sources Strategy</Link> and <Link href="/services/seo-aeo-geo/" className="text-[#00D4AA] underline underline-offset-4 hover:text-white">SEO, AEO and GEO</Link> support the wider work.</p>
        </section>

        <section className="border-y border-white/10 bg-white/[0.03] py-16">
          <div className="container max-w-4xl px-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF8C00]">Frequently asked questions</p>
            <h2 className="mt-3 font-serif text-3xl">AI Citation Programme FAQs</h2>
            <FaqAccordion items={faqs} headingLevel={3} className="mt-8 space-y-3" itemClassName="overflow-hidden rounded-lg border border-white/10 bg-[#0F1419]" buttonClassName="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.04]" questionClassName="m-0 text-base font-semibold text-white" answerClassName="px-5 pb-5" answerTextClassName="text-sm leading-relaxed text-white/65" iconClassName="h-5 w-5 shrink-0 text-[#FF8C00]" />
          </div>
        </section>

        <section className="container max-w-5xl px-4 py-16">
          <div className="rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/[0.06] p-6 md:p-8">
            <p className="text-sm font-bold text-[#00D4AA]">1. Check my AI visibility</p>
            <div className="mt-4"><ScoreInlineForm placement="citation_end" /></div>
            <ScoreBookingLink context={{ placement: "citation_end", nextIntent: "citation", pageType: "service", niche: "ai_citation_programme", bookingSource }} className="mt-5 inline-flex text-sm font-semibold text-[#FFB84D] underline underline-offset-4 hover:text-white">2. After your score, talk it through with Modi <ArrowRight className="ml-2 h-4 w-4" /></ScoreBookingLink>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-white/50">ChatGPT is a trade mark of OpenAI. Gemini is a trade mark of Google LLC. Integrated.Social is not affiliated with or endorsed by OpenAI or Google.</p>
        </section>
      </main>
      <button type="button" onClick={focusScoreField} className="fixed inset-x-4 bottom-4 z-40 min-h-12 rounded-lg bg-[#FF8C00] px-5 text-sm font-bold text-[#0F1419] shadow-lg md:hidden">Check my AI visibility</button>
      <Footer />
    </div>
  );
}

function ScoreLinks({ onFocus, bookingSource }: { onFocus: () => void; bookingSource: "hero_link" | "report_email" }) {
  return (
    <div className="mt-7 flex flex-wrap items-center gap-4">
      <button type="button" onClick={onFocus} className="inline-flex min-h-11 items-center rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#0F1419] hover:bg-[#FFB84D]">Check my AI visibility</button>
      <ScoreBookingLink context={{ placement: "citation_hero", nextIntent: "citation", pageType: "service", niche: "ai_citation_programme", bookingSource }} className="text-sm font-semibold text-[#FFB84D] underline underline-offset-4 hover:text-white">After your score, talk it through with Modi</ScoreBookingLink>
    </div>
  );
}
